const DEFAULT_FUNDRAISING_STATUSES = ["FUNDING", "PAUSED"];
const DEFAULT_SECONDARY_STATUSES = ["LOCK_IN"];
const DEFAULT_FUNDED_STATUSES = ["LOCK_IN"];
/** Post-active listing: matured, fully redeemed to treasury, or administratively closed. */
const DEFAULT_POST_MARKET_PROJECT_STATUSES = ["MATURED", "REDEEMED", "CLOSED"];

export function normalizeProjectStatus(status) {
  const normalized = String(status || "").trim().toUpperCase();
  if (normalized === "OPEN") return "REVIEW";
  if (normalized === "MATURED") return "MATURED";
  if (normalized === "COMPLETED") return "REDEEMED";
  if (normalized === "REDEEMED") return "REDEEMED";
  return normalized;
}

/** Hide draft/open prep, cancelled, and closed listings from investor browse surfaces. */
const INVESTOR_HIDDEN_CATALOG_STATUSES = new Set(["DRAFT", "REVIEW", "OPEN", "CLOSED"]);

export function isCancelledProjectStatus(status) {
  return normalizeProjectStatus(status) === "CANCELLED";
}

/** Hide draft/open prep, cancelled, and closed projects from investor browse surfaces. */
export function filterInvestorCatalogProjects(projects) {
  return (projects || []).filter((p) => {
    const st = normalizeProjectStatus(p?.status);
    if (INVESTOR_HIDDEN_CATALOG_STATUSES.has(st)) return false;
    if (st === "CANCELLED") return false;
    return true;
  });
}

/** Display codes like N01 (zero-pad numeric segment when letters + digits). */
export function formatProjectCodeDisplay(codeRaw) {
  const raw = String(codeRaw || "").trim().toUpperCase();
  const compact = raw.replace(/[^A-Z0-9]/g, "");
  const m = compact.match(/^([A-Z]+)(\d+)$/);
  if (!m) return raw || "";
  return `${m[1]}${m[2].padStart(2, "0")}`;
}

/** Sort key: letter prefix + numeric segment (N01 before N10), then name; no-code projects last. */
export function projectCodeSortKey(item) {
  const codeRaw = item?.project_code ?? item?.projectCode ?? "";
  const name = String(item?.project_name ?? item?.name ?? "");
  const raw = String(codeRaw || "").trim().toUpperCase();
  const compact = raw.replace(/[^A-Z0-9]/g, "");
  const m = compact.match(/^([A-Z]+)(\d+)$/);
  if (m) {
    return [0, m[1], parseInt(m[2], 10), name.toLowerCase()];
  }
  if (compact) {
    return [1, compact, 0, name.toLowerCase()];
  }
  return [2, "", 0, name.toLowerCase()];
}

export function compareProjectByCode(a, b) {
  const ka = projectCodeSortKey(a);
  const kb = projectCodeSortKey(b);
  for (let i = 0; i < ka.length; i += 1) {
    if (ka[i] < kb[i]) return -1;
    if (ka[i] > kb[i]) return 1;
  }
  return 0;
}

export function sortProjectsByCode(projects) {
  return [...(projects || [])].sort(compareProjectByCode);
}

function projectCreatedAtMs(item) {
  const raw = item?.created_at ?? item?.createdAt;
  if (!raw) return 0;
  const t = Date.parse(String(raw));
  return Number.isFinite(t) ? t : 0;
}

/** Newest projects first (created_at desc), then highest project code (N10 before N01). */
export function compareProjectsNewestFirst(a, b) {
  const ta = projectCreatedAtMs(a);
  const tb = projectCreatedAtMs(b);
  if (ta !== tb) return tb - ta;
  return -compareProjectByCode(a, b);
}

export function sortProjectsNewestFirst(projects) {
  return [...(projects || [])].sort(compareProjectsNewestFirst);
}

function parseProjectNameAndCode(project) {
  const rawName = String(project?.name || "").trim();
  const prefixedNameMatch = rawName.match(/^([A-Za-z]+)\s*[-–—]?\s*(\d+)\s*:\s*(.+)$/);
  const codeFromName = prefixedNameMatch
    ? formatProjectCodeDisplay(`${prefixedNameMatch[1]}${prefixedNameMatch[2]}`)
    : "";
  let name = prefixedNameMatch ? String(prefixedNameMatch[3] || "").trim() : rawName;
  const code =
    formatProjectCodeDisplay(project?.project_code ?? project?.projectCode) || codeFromName;
  if (code && name) {
    const prefix = new RegExp(`^${code.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*:\\s*`, "i");
    if (prefix.test(name)) name = name.replace(prefix, "").trim();
  }
  return { code, name };
}

/** Project name only (no code prefix) — use when code is shown in its own column. */
export function projectNameOnly(project) {
  const { code, name } = parseProjectNameAndCode(project);
  return name || code || "—";
}

/**
 * Title for project pickers and cards: "N06: North City Hospital Ltd" (project no + name).
 * Uses ``project_code`` when set; otherwise parses a leading "N6 : Name" pattern from stored name.
 */
export function projectTitleWithCode(project) {
  const { code, name } = parseProjectNameAndCode(project);
  if (code && name) return `${code}: ${name}`;
  if (name) return name;
  return code || "Project";
}

/** Public / authenticated project landing path (slug when published, else by id). */
export function projectLandingPath(project) {
  if (!project) return "";
  const slug = String(project.landing_slug || "").trim();
  if (slug) return `/p/${encodeURIComponent(slug)}`;
  if (project.id != null && project.id !== "") {
    return `/projects/${encodeURIComponent(project.id)}/about`;
  }
  return "";
}

/** True when the project is past active fundraising/secondary (matured, redeemed, or closed). */
export function isCompletedProjectStatus(status) {
  return DEFAULT_POST_MARKET_PROJECT_STATUSES.includes(normalizeProjectStatus(status));
}

/** Admin “Redeemed & closed” table: terminal SSA outcomes only (not Matured operations). */
export function isRedeemedOrClosedProjectStatus(status) {
  const st = normalizeProjectStatus(status);
  return st === "REDEEMED" || st === "CLOSED";
}

export function getProjectLifecycleLabel(status) {
  const normalized = normalizeProjectStatus(status);
  if (["FUNDING", "PAUSED"].includes(normalized)) return "Fundraising";
  if (["LOCK_IN"].includes(normalized)) return "Funded";
  if (isCancelledProjectStatus(normalized)) return "Cancelled";
  if (normalized === "REDEEMED") return "Redeemed";
  if (normalized === "MATURED") return "Matured";
  if (normalized === "CLOSED") return "Closed";
  if (normalized === "REVIEW") return "Review";
  if (normalized === "DRAFT") return "Draft";
  return "Unmapped";
}

/** CSS tone class for stage text (e.g. Fundraising / Funded) on investor surfaces. */
export function getMarketStageToneClass(status) {
  const normalized = normalizeProjectStatus(status);
  if (["FUNDING", "PAUSED"].includes(normalized)) return "invx-market-stage--fundraising";
  if (normalized === "LOCK_IN") return "invx-market-stage--funded";
  if (isCancelledProjectStatus(normalized)) return "invx-market-stage--cancelled";
  if (normalized === "REDEEMED") return "invx-market-stage--redeemed";
  if (normalized === "MATURED" || normalized === "CLOSED") return "invx-market-stage--completed";
  if (normalized === "REVIEW") return "invx-market-stage--open";
  if (normalized === "DRAFT") return "invx-market-stage--draft";
  return "invx-market-stage--default";
}

function parseStatusList(raw, fallback) {
  const items = String(raw || "")
    .split(",")
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean);
  return items.length > 0 ? items : fallback;
}

export function buildProjectViewGroups(projects = [], settings = null) {
  const fundraisingStatuses = parseStatusList(settings?.fundraising_secondary_statuses, DEFAULT_FUNDRAISING_STATUSES);
  const secondaryStatuses = parseStatusList(settings?.secondary_market_statuses, DEFAULT_SECONDARY_STATUSES);
  const fundedStatuses = parseStatusList(settings?.funded_overview_statuses, DEFAULT_FUNDED_STATUSES);

  const notCancelled = (p) => !isCancelledProjectStatus(p?.status);
  const isFunded = (p) =>
    Number(p?.shares_sold || 0) >= Number(p?.total_shares || 0) || fundedStatuses.includes(normalizeProjectStatus(p?.status));
  const isFundraising = (p) => fundraisingStatuses.includes(normalizeProjectStatus(p?.status));
  const isSecondary = (p) => isFunded(p) && secondaryStatuses.includes(normalizeProjectStatus(p?.status));

  return {
    fundraisingAndSecondaryProjects: projects.filter(
      (p) => notCancelled(p) && (isFundraising(p) || isSecondary(p))
    ),
    fundedOverviewProjects: projects.filter((p) => notCancelled(p) && isFunded(p)),
  };
}

/** Admin: funded / fully-sold projects cannot edit details, PDFs, or share structure. */
export function isAdminProjectContentLocked(project, lifecycleBuckets = null) {
  const st = normalizeProjectStatus(project?.status);
  if (["LOCK_IN", "MATURED", "REDEEMED", "CLOSED"].includes(st)) return true;
  const fundedIds = lifecycleBuckets?.funded;
  if (fundedIds && project?.id != null) {
    const id = project.id;
    if (Array.isArray(fundedIds)) {
      if (fundedIds.some((p) => String(p?.id ?? p) === String(id))) return true;
    } else if (typeof fundedIds.has === "function" && fundedIds.has(id)) {
      return true;
    }
  }
  const total = Number(project?.total_shares || 0);
  const sold = Number(project?.shares_sold || 0);
  return total > 0 && sold >= total;
}

/** Hard-lock share price + total shares after first primary sale (fundraising option A). */
export function isAdminProjectOfferTermsLocked(project, lifecycleBuckets = null) {
  if (isAdminProjectContentLocked(project, lifecycleBuckets)) return true;
  const sold = Number(project?.shares_sold || 0);
  return sold > 0;
}

export function buildLifecycleBuckets(projects = [], settings = null) {
  const fundraisingStatuses = new Set(
    parseStatusList(settings?.fundraising_secondary_statuses, DEFAULT_FUNDRAISING_STATUSES).filter(
      (s) => !isCompletedProjectStatus(s) && !isCancelledProjectStatus(s)
    )
  );
  const fundedStatuses = new Set(
    parseStatusList(settings?.funded_overview_statuses, DEFAULT_FUNDED_STATUSES).filter(
      (s) => !isCompletedProjectStatus(s) && !isCancelledProjectStatus(s)
    )
  );

  const cancelled = projects.filter((p) => isCancelledProjectStatus(p?.status));
  const cancelledIds = new Set(cancelled.map((p) => p.id));

  const completed = projects.filter(
    (p) => !cancelledIds.has(p.id) && isCompletedProjectStatus(p.status)
  );
  const completedIds = new Set(completed.map((p) => p.id));

  const isFullySold = (p) => {
    const total = Number(p?.total_shares || 0);
    const sold = Number(p?.shares_sold || 0);
    return total > 0 && sold >= total;
  };

  // "Funded" for investors = fully sold out, regardless of whether the backend status
  // has already transitioned to LOCK_IN.
  const funded = projects.filter((p) => {
    if (cancelledIds.has(p.id) || completedIds.has(p.id)) return false;
    return fundedStatuses.has(normalizeProjectStatus(p.status)) || isFullySold(p);
  });
  const fundedIds = new Set(funded.map((p) => p.id));
  const fundraising = projects.filter(
    (p) =>
      !cancelledIds.has(p.id) &&
      !completedIds.has(p.id) &&
      !fundedIds.has(p.id) &&
      fundraisingStatuses.has(normalizeProjectStatus(p.status))
  );

  return { fundraising, funded, completed, cancelled };
}

/** Active fundraising + funded projects eligible for asset manager assignment (deduped). */
export function buildManagerAssignmentRows(lifecycleBuckets) {
  const byId = new Map();
  for (const p of [...(lifecycleBuckets?.fundraising || []), ...(lifecycleBuckets?.funded || [])]) {
    if (p?.id) byId.set(p.id, p);
  }
  return Array.from(byId.values());
}

/**
 * Annual expected / estimated return % from project payloads (serializer may expose snake_case + aliases).
 * Skips *_actual_* / realized performance metrics so NAV history is not mistaken for target yield.
 */
function coercePercentLikeNumber(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw)
    .replace(/,/g, "")
    .replace(/%/g, "")
    .trim();
  if (s === "") return null;
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

export function parseProjectExpectedAnnualReturnPct(project) {
  if (!project || typeof project !== "object") return null;
  const ordered = [
    project.estimated_return_pct,
    project.estimatedReturnPct,
    project.expected_return_pct,
    project.expectedReturnPct,
  ];
  for (const raw of ordered) {
    const n = coercePercentLikeNumber(raw);
    if (n != null) return n;
  }

  for (const [key, raw] of Object.entries(project)) {
    const kl = String(key).toLocaleLowerCase();
    if (raw == null || raw === "") continue;
    if (typeof raw === "object" && !Array.isArray(raw)) continue;
    if (kl.includes("actual") || kl.includes("annualized_return") || kl.includes("performance")) continue;
    if (!/(expected|estimated|projected|target)/.test(kl)) continue;
    if (!/(return|pct|percent|yield)/.test(kl)) continue;
    const n = coercePercentLikeNumber(raw);
    if (n != null) return n;
  }
  return null;
}

/** e.g. `10.5% (p.a.)` for investment cards; returns `null` when missing so callers can show "—". */
export function formatExpectedReturnPctWithPa(project) {
  const n = parseProjectExpectedAnnualReturnPct(project);
  if (n == null) return null;
  const pct = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
    useGrouping: false,
  }).format(n);
  return `${pct}% (p.a.)`;
}